import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ApiConfig from "../../api/ApiConfig";
import { GetWithAuth } from "../../api/ApiServices";
import { AppContext } from "../../context/AppContext";
import veg_icon from "../../assets/images/icons/veg_icon.svg";
import nonveg_icon from "../../assets/images/icons/nonveg_icon.svg";
import dr_2 from "../../assets/images/biry.jpg";
import Subheader from "../../sharedComponent/Subheader";
import HomeMenuSection from "./HomeMenuSection";

// ── Filter config ─────────────────────────────────────────────────────────────
const DIETARY_FILTERS = [
  { key: "all",     label: "All Items" },
  { key: "veg",     label: "Veg",     cls: "veg-filter",    icon: veg_icon },
  { key: "non-veg", label: "Non-Veg", cls: "nonveg-filter", icon: nonveg_icon },
  { key: "Recommended",     label: "Recommended" },
  { key: "Popular",     label: "Popular" },
];

const SORT_OPTIONS = [
  { key: "price_asc",  label: "Low to High" },
  { key: "price_desc", label: "High to Low" },
  // { key: "available",  label: "Available" },
];

// ── Component ─────────────────────────────────────────────────────────────────
const MenuPage = () => {
  const {
    selectedStore,
    MenuCategoryList,
    setMenuCategoryList,
    defaultImgs,
  } = useContext(AppContext);

  const navigate = useNavigate();
  const { state: routeState } = useLocation();

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [dietaryFilter,      setDietaryFilter]      = useState("all");
  const [activeSort,         setActiveSort]         = useState(null);
  const [takeaway,           setTakeaway]           = useState(false);
  const [readyToUse,         setReadyToUse]         = useState(false);
  const [searchInput,        setSearchInput]        = useState("");
  const [search,             setSearch]             = useState("");
  const [cartCount,          setCartCount]          = useState(0);

  const defaultCatImg = defaultImgs?.category_image || dr_2;

  // ── Cart count (refreshed by HomeMenuSection via onCartChange) ─────────────
  const loadCart = useCallback(async () => {
    try {
      const res = await GetWithAuth(ApiConfig.CART_LIST);
      if (res?.success) {
        setCartCount(res.data.reduce((acc, i) => acc + i.order_quantity, 0));
      }
    } catch (_) {}
  }, []);

  // ── Category list ──────────────────────────────────────────────────────────
  const loadCategories = useCallback(async () => {
    if (!selectedStore) return;
    try {
      const res = await GetWithAuth(
        `${ApiConfig.GET_MENU_CATEGORY}?restaurant_id=${selectedStore}`
      );
      if (res?.success) setMenuCategoryList(res.data || []);
    } catch (_) {}
  }, [selectedStore]);

  useEffect(() => {
    loadCart();
    loadCategories();
  }, [selectedStore]);
  

  // Activate filter based on capacity_type passed from navigation state
  useEffect(() => {
    if (routeState?.capacity_type === "kitchen_capacity") setReadyToUse(true);
    if (routeState?.capacity_type === "delivery_capacity")    setTakeaway(true);
  }, [routeState]);

  // Debounce search input — only fire API after user stops typing for 400ms
  useEffect(() => {
    const tid = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(tid);
  }, [searchInput]);

  const handleCategoryClick = (catId) => {
    setSelectedCategoryId(prev => (prev === catId ? null : catId));
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="menu-page-wrapper" id="cx-main">
      <Subheader />

      {/* ── Page header ── */}
      <div className="mc-page-header">
        <div className="mc-page-header-inner">
          <h5 className="mc-page-title">Our Menu</h5>
          <input
            className="mc-search-input"
            type="text"
            placeholder="Search items…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {/* ── Category chips ── */}
      {MenuCategoryList?.length > 0 && (
        <div className="mc-category-chips-row">
          <button
            className={`mc-cat-chip${!selectedCategoryId ? " active" : ""}`}
            onClick={() => setSelectedCategoryId(null)}
          >
            All
          </button>
          {MenuCategoryList.map(cat => (
            <button
              key={cat.category_id}
              className={`mc-cat-chip${selectedCategoryId === cat.category_id ? " active" : ""}`}
              onClick={() => handleCategoryClick(cat.category_id)}
            >
              <img
                src={
                  cat.category_image_url && cat.category_image_url !== "null"
                    ? cat.category_image_url
                    : defaultCatImg
                }
                alt={cat.category_name}
                onError={e => { e.target.src = defaultCatImg; }}
              />
              {cat.category_name}
            </button>
          ))}
        </div>
      )}

      {/* ── Filter bar ── */}
      <div className="mc-filter-bar">
        {DIETARY_FILTERS.map(f => (
          <button
            key={f.key}
            className={`mc-filter-chip${f.cls ? ` ${f.cls}` : ""}${dietaryFilter === f.key ? " active" : ""}`}
            onClick={() => setDietaryFilter(f.key)}
          >
            {f.icon && <img src={f.icon} width={16} height={16} alt={f.label} />}
            {f.label}
          </button>
        ))}

        <div className="mc-filter-divider" />

        {SORT_OPTIONS.map(s => (
          <button
            key={s.key}
            className={`mc-filter-chip${activeSort === s.key ? " active" : ""}`}
            onClick={() => setActiveSort(prev => (prev === s.key ? null : s.key))}
          >
            {s.key === "price_asc"  && <span className="mc-sort-arrow">↑</span>}
            {s.key === "price_desc" && <span className="mc-sort-arrow">↓</span>}
            {s.label}
          </button>
        ))}

        <div className="mc-filter-divider" />

        <button
          className={`mc-filter-chip${takeaway ? " active" : ""}`}
          onClick={() => setTakeaway(prev => !prev)}
        >
          Takeaway
        </button>
        <button
          className={`mc-filter-chip${readyToUse ? " active" : ""}`}
          onClick={() => setReadyToUse(prev => !prev)}
        >
          Ready to Eat
        </button>
      </div>

      {/* ── Menu grid with infinite scroll ── */}
      <HomeMenuSection
        categoryId={selectedCategoryId}
        dietaryFilter={dietaryFilter}
        activeSort={activeSort}
        takeaway={takeaway}
        readyToUse={readyToUse}
        search={search}
        onCartChange={loadCart}
        showEmptyState
        emptyAction={
          (dietaryFilter !== "all" || activeSort || takeaway || readyToUse || search) && (
            <button
              className="button-main"
              style={{ marginTop: "16px" }}
              onClick={() => { setDietaryFilter("all"); setActiveSort(null); setTakeaway(false); setReadyToUse(false); setSearchInput(""); setSearch(""); }}
            >
              Clear Filters
            </button>
          )
        }
      />

      {/* ── Floating view-cart button ── */}
      {cartCount > 0 && (
        <div className="mc-floating-cart-wrapper">
          <button
            className="mc-floating-cart-btn"
            onClick={() => navigate("/Cart")}
          >
            🛒 View Cart &nbsp;({cartCount})
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuPage;
